import { useAuth } from '@/app/AuthContext.jsx';
import { WHATSAPP_NUMBER } from '@/app/config.js';
import {
  useActiveTaskByCase,
  useAssignCaseAssigneeMutation,
  useCanAssignCase,
  useCaseDetail,
  useDocumentTitle,
  useMembers,
  useNotificationActings,
  useTasksList,
} from '@/hooks';
import {
  diligenciaDisplay,
  formatDateDMY,
  mapOrgMembers,
  taskDiligenciaStage,
} from '@/lib';
import { qk } from '@/lib/queryKeys';
import { formatDeadlineDate } from '@/lib/dateUtils';
import {
  apiErrorName,
  deleteCase,
  ensureCaseScrape,
} from '@/lib/recepthor-api';
import { StatusDotPill } from '@/shared';
import { LedgerRowSkeleton, useConfirm, useToast } from '@/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  CausaDetalle,
  CausaNotFound,
  CausaReadFailed,
} from './CausaDetalleVista';
import { admitsAssignee, buildRol } from './causaDisplay';

import { extractList } from '@/lib/apiList';
import AsignarEncargadoModal from './AsignarEncargadoModal.jsx';

const formatDate = formatDateDMY;

// Maps a raw case into the detail view shape. Competencia/procedimiento/
// estado feed the head's meta band (with encargado); etapa/ingreso/libro/
// ubicación/últ. receptor feed the "Datos de la causa" aside. Only fields the
// case actually carries — no fabricated narrative or claims.
//
// Exported for its own test: it is where a missing field decides between "—" and
// a wrong value, and that decision deserves a check without rendering the page.
export function mapCausa(raw) {
  if (!raw) return null;
  const c = raw.case ?? raw;

  return {
    rol: buildRol(c),
    // The corte is a different entity of the PJUD hierarchy, so it must never
    // stand in for the tribunal: a fallback to it would read as a correct value.
    // The corte has its own field right below, so nothing is lost by omitting it
    // here.
    tribunal: c.tribunalName || c.tribunal?.name || '—',
    corte: c.courtName || c.court?.name || null,
    partes: c.title || c.partes || '—',
    competencia: c.competence || '—',
    // Estado of the CASE (legal status from PJUD), not the scrape verification
    // status nor any task status.
    estado: c.legalStatus || '—',
    etapa: c.caseStage || '—',
    procedimiento: c.procedure || '—',
    // "Ubicación" not "Comuna": the real field is location/modality (e.g.
    // "Digital"), not a strict comuna.
    ubicacion: c.location || '—',
    libro: c.bookType || c.book || '—',
    fechaIngreso: formatDate(c.admissionDate),
  };
}

// The toast for a failed assignee change. The UI hides the control on
// NOT_EXIST and VERIFIED_FAIL, and a stale read can still reach the API's 409
// caseNotEditable on those states.
//
// Exported for its own test, as mapCausa above is.
export function assignFailureMessage(err) {
  if (apiErrorName(err?.body) === 'caseNotEditable') {
    return 'No se puede cambiar el encargado de una causa que no aparece en el PJUD. Escríbenos para gestionarla.';
  }
  return `No se pudo asignar el encargado: ${err?.message ?? 'error desconocido'}`;
}

const NOTIF_STAGE_STATUS = {
  Notificación: 'info',
  Resolución: 'warn',
  Sentencia: 'ok',
  Trámite: 'info',
};

// Actuación = a PJUD acting on the case timeline (the "Actuaciones" tab).
function mapActuacion(raw) {
  const a = raw.acting ?? raw;
  const stageLabel = a.stage || '—';
  const stageStatus = NOTIF_STAGE_STATUS[stageLabel] ?? 'info';
  return {
    id: a.id ?? raw.id,
    fecha: formatDate(a.actingDate || a.createdAt),
    titulo: a.name || a.title || '—',
    fileUrl: a.file?.url ?? null,
    stage:
      stageLabel === '—' ? (
        '—'
      ) : (
        <StatusDotPill status={stageStatus} label={stageLabel} />
      ),
  };
}

// Receptor withdrawal row, newest first, which feeds "Últ. receptor" in the
// aside. Source: the case's own receptor panel scraped from PJUD
// (case_receptor_notifications, sent as `receptorNotifications` on the case
// payload). It is activity on this case, and the court-wide registry is a
// separate source.
function mapReceptorNotification(raw, i) {
  return {
    id: `${raw.withdrawalDate ?? ''}-${i}`,
    receptor: raw.withdrawalData || '—',
    cuaderno: raw.notebook || '—',
    retiro: formatDate(raw.withdrawalDate),
    estado: raw.status || '—',
  };
}

// Estado through the SOT derivation (deadline + dateOffered included), so this
// tab tells the same story as /diligencias and the detail: a QUOTING past its
// plazo reads Expirada, an IN_PROGRESS past the committed date reads Atrasada.
// The old hand-rolled map keyed on a stored EXPIRED status that never persists.
function mapDiligencia(raw) {
  const t = raw.task ?? raw;
  const d = diligenciaDisplay(t.stage, t.deadline, Date.now(), {
    deadlineExpiresAt: t.deadlineExpiresAt,
    dateOffered: t.dateOffered ?? null,
  });
  return {
    id: t.id ?? raw.id,
    fecha: t.deadline
      ? (formatDeadlineDate(t.deadline, 'dmy') ?? '—')
      : formatDate(t.createdAt),
    estado: <StatusDotPill status={d.dot} label={d.label} />,
    receptor: t.receptorName || '—',
  };
}

function receptorOf(t) {
  return (
    t?.receptorName ||
    t?.receptor?.fullName ||
    t?.receptor?.name ||
    t?.receptor ||
    null
  );
}

const VALID_TABS = ['resumen', 'actuaciones', 'diligencias'];
const TAB_LABELS = {
  resumen: 'Resumen',
  actuaciones: 'Actuaciones',
  diligencias: 'Diligencias',
};

export default function CausaDetalleRoute() {
  const { caseId } = useParams();
  useDocumentTitle('Causa');
  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumb, setPills } = useOutletContext();
  const { organizationId, activeCapability } = useAuth();
  // Solo case:assign holders ven asignar/cambiar; en org individual el
  // encargado es implícito (el API lo auto-asigna) y no hay controles.
  const canAssign = useCanAssignCase();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [showAsignar, setShowAsignar] = useState(false);
  const [busy, setBusy] = useState(false);
  // Drives the spinning sync icon while a manual re-scrape is in flight (kept
  // separate from `busy`, which also guards the delete path).
  const [syncing, setSyncing] = useState(false);

  // Active tab lives in the URL (?tab=) so it survives reload and is reflected
  // in the breadcrumb / shareable links.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : 'resumen';
  const handleTabChange = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  // Org members — para el modal de asignar encargado (solo con permiso; el
  // nombre del encargado ya llega resuelto en assigneeName).
  const { data: membersData } = useMembers(organizationId, {
    enabled: organizationId != null && canAssign,
  });
  const members = useMemo(
    () => (membersData ? mapOrgMembers(extractList(membersData)) : []),
    [membersData],
  );

  // Assign-assignee mutation → PATCH /cases/:caseId/assignee.
  const assignMutation = useAssignCaseAssigneeMutation(caseId, {
    onSuccess: () => {
      setShowAsignar(false);
      toast({ kind: 'success', message: 'Encargado asignado.' });
    },
    onError: (err) => {
      toast({ kind: 'err', message: assignFailureMessage(err) });
    },
  });

  const {
    data: caseData,
    isLoading: caseLoading,
    isError: caseFailed,
    refetch: refetchCase,
  } = useCaseDetail(caseId);

  const {
    data: notifData,
    isLoading: notifLoading,
    isError: actingsFailed,
  } = useNotificationActings(caseId, { enabled: !!caseId });

  // Diligencia activa (aside card). One active diligencia per causa.
  const { data: activeTaskData } = useActiveTaskByCase(caseId, {
    enabled: !!caseId,
  });

  // Tasks for this case — the server filters by caseId, so the page fetches
  // only this case's tasks instead of the whole org list.
  const { data: allTasksData, isLoading: tasksLoading } = useTasksList(
    { page: 1, pageSize: 50, caseId },
    { enabled: !!caseId },
  );

  const isLoading = caseLoading || notifLoading || tasksLoading;

  const causaRaw = caseData ?? null;
  const causaBase = useMemo(() => mapCausa(causaRaw), [causaRaw]);

  // Server-scoped by caseId; no client-side re-filter.
  const forCase = useMemo(
    () => (allTasksData ? extractList(allTasksData) : []),
    [allTasksData],
  );

  const diligencias = useMemo(() => forCase.map(mapDiligencia), [forCase]);

  const actuaciones = useMemo(
    () => (notifData ? extractList(notifData).map(mapActuacion) : []),
    [notifData],
  );

  // Receptor activity of THIS causa (PJUD receptor panel), newest retiro first.
  const receptores = useMemo(() => {
    const c = causaRaw?.case ?? causaRaw;
    const list = Array.isArray(c?.receptorNotifications)
      ? [...c.receptorNotifications]
      : [];
    list.sort(
      (a, b) =>
        new Date(b.withdrawalDate ?? 0) - new Date(a.withdrawalDate ?? 0),
    );
    return list.map(mapReceptorNotification);
  }, [causaRaw]);

  const activeTask = useMemo(
    () => (activeTaskData ? (extractList(activeTaskData)[0] ?? null) : null),
    [activeTaskData],
  );

  const activeDiligencia = useMemo(() => {
    if (!activeTask) return null;
    const stage = taskDiligenciaStage(activeTask);
    return {
      id: activeTask.id,
      tone: stage.tone,
      label: stage.label,
      receptor: receptorOf(activeTask),
      plazo: formatDeadlineDate(activeTask.deadline, 'dmy') ?? '—',
    };
  }, [activeTask]);

  // Últ. receptor: the active/most-recent task's receptor; else the latest
  // retiro from the causa's own PJUD receptor panel (receptores is sorted
  // newest-first above).
  const ultReceptor = useMemo(() => {
    const fromActive = activeDiligencia?.receptor;
    if (fromActive) return fromActive;
    const fromTasks = forCase
      .map((raw) => receptorOf(raw.task ?? raw))
      .find(Boolean);
    if (fromTasks) return fromTasks;
    const fromPanel = receptores.find((r) => r.receptor !== '—');
    return fromPanel?.receptor || null;
  }, [forCase, activeDiligencia, receptores]);

  const causa = useMemo(
    () => (causaBase ? { ...causaBase, ultReceptor } : null),
    [causaBase, ultReceptor],
  );

  useEffect(() => {
    // Prefer the human-legible rol ("C-1234-2026") and fall back to the
    // case title; only show the raw UUID while the case data is still loading.
    const breadcrumbLeaf =
      causa?.rol ||
      causa?.partes ||
      (caseId ? caseId.slice(0, 8).toUpperCase() : '…');
    setBreadcrumb([
      { label: 'Causas', onClick: () => navigate('/causas') },
      breadcrumbLeaf,
      TAB_LABELS[activeTab],
    ]);
    // Primary action lives in the page head, not duplicated in the topbar.
    setPills([]);
    return () => {
      setBreadcrumb([]);
      setPills([]);
    };
  }, [setBreadcrumb, setPills, causa, caseId, activeTab, navigate]);

  if (isLoading) {
    return (
      <div className="ledger-body">
        {[...Array(5)].map((_, i) => (
          <LedgerRowSkeleton key={i} />
        ))}
      </div>
    );
  }

  // A read that failed is not a causa that does not exist. Saying the rol
  // belongs to no subscribed causa about a request that never arrived is a
  // statement the page cannot support, and the sync is when the reads are most
  // likely to fail.
  if (caseFailed) {
    return (
      <CausaReadFailed
        onRetry={() => refetchCase()}
        onBack={() => navigate('/causas')}
      />
    );
  }

  if (!causa) {
    return <CausaNotFound onBack={() => navigate('/causas')} />;
  }

  // Nombre del encargado: el backend lo resuelve en assigneeName (GET
  // /cases/:id), así TODO miembro lo ve — la lista de miembros es admin-gated.
  // Fallback vía members para respuestas de un API anterior sin assigneeName.
  const rawCase = causaRaw?.case ?? causaRaw;
  const encargadoId = rawCase?.assigneeId ?? null;
  const encargadoMember = encargadoId
    ? members.find((m) => String(m.id) === String(encargadoId))
    : null;
  const encargadoNombre =
    rawCase?.assigneeName ?? encargadoMember?.nombre ?? null;

  // tombstoned = the case was archived/deleted (TODO: confirm the field
  // name with the API). Manual re-sync applies to syncing cases (CREATED /
  // AWAITING_RESPONSE) and to RESPONSE_ERROR (a retryable scraper failure);
  // VERIFIED_OK is already subscribed (nothing to re-scrape) and NOT_EXIST means
  // the causa doesn't exist in PJUD, so re-scraping won't make it appear — only
  // delete is offered (VERIFIED_FAIL is the deprecated predecessor of NOT_EXIST).
  const tombstoned = Boolean(
    rawCase?.tombstone ??
    rawCase?.tombstonedAt ??
    rawCase?.archivedAt ??
    rawCase?.deletedAt,
  );
  const verif = (rawCase?.verificationStatus || '').toUpperCase();
  const canResync =
    !tombstoned &&
    (verif === 'CREATED' ||
      verif === 'AWAITING_RESPONSE' ||
      verif === 'RESPONSE_ERROR');

  // Diligencias only make sense on a causa the scraper confirmed exists in
  // PJUD (VERIFIED_OK). While syncing, when the causa doesn't exist, or after a
  // scraper error, the "Nueva diligencia" buttons render disabled with the reason.
  const nuevaDisabledReason =
    verif === 'VERIFIED_OK'
      ? null
      : verif === 'NOT_EXIST' || verif === 'VERIFIED_FAIL'
        ? 'Para pedir una diligencia en esta causa, escríbenos por WhatsApp o correo.'
        : verif === 'RESPONSE_ERROR'
          ? 'Hubo un error al consultar el PJUD; se reintentará antes de poder crear diligencias.'
          : 'Esperando la confirmación del PJUD para crear diligencias.';

  // The scrape state the call left behind, written onto the cached causa.
  //
  // `relaunched` is not read. The control that reaches here only renders for a
  // causa the scrape can move (CREATED, AWAITING_RESPONSE, RESPONSE_ERROR), and
  // with force the API relaunches every one of those. The only value that
  // answers false is a causa the PJUD does not have, and that one never shows
  // the control, so a branch for it would be unreachable.
  //
  // Re-reading instead would answer the row as it was BEFORE the scrape, which
  // is the whole defect: the launch is asynchronous, so a read that follows it
  // by milliseconds returns the previous verdict, and the screen then shows a
  // settled causa while a scrape is in flight. The response already carries the
  // verdict after the call, so nothing has to be waited for or invented.
  function applyScrapeState(state) {
    if (!state?.verificationStatus) return;
    queryClient.setQueryData(qk.caseDetail(caseId), (prev) => {
      if (!prev) return prev;
      const patch = {
        scrapeDetail: state.scrapeDetail ?? null,
        scrapeRequestedAt: state.scrapeRequestedAt ?? null,
        syncedAt: state.syncedAt ?? null,
        verificationStatus: state.verificationStatus,
      };
      // The endpoint answers either the causa or an envelope around it, and the
      // detail is cached in whichever shape it arrived.
      return prev.case
        ? { ...prev, case: { ...prev.case, ...patch } }
        : { ...prev, ...patch };
    });
  }

  // Sync = re-launch the PJUD scrape via POST /cases/:id/scrape with
  // force=true: an explicit manual action, so it re-launches regardless of
  // state. The backend resolves the PJUD params from the stored case — the
  // front only sends the id. Since the re-scrape is async, on resolve we say
  // "solicitada", not "synced".
  async function handleRetry() {
    if (busy) return;
    setBusy(true);
    setSyncing(true);
    const toastId = toast({
      kind: 'loading',
      message: 'Sincronizando…',
      dismissLabel: 'en curso',
    });
    let failed = false;
    let state = null;
    try {
      const res = await ensureCaseScrape(caseId, { force: true });
      state = res?.data ?? res ?? null;
    } catch (err) {
      failed = true;
      console.error('ensureCaseScrape failed', err);
    }
    // The list and the actings are re-read; the causa is not, because its state
    // after the call is what the call answered.
    await Promise.allSettled([
      queryClient.invalidateQueries({ queryKey: qk.cases.all() }),
      queryClient.invalidateQueries({
        queryKey: qk.notificationActings.all(caseId),
      }),
    ]);
    applyScrapeState(state);
    setSyncing(false);
    setBusy(false);
    // The sync is fire-and-forget; its success/failure is a PJUD-scrape
    // concern owned by backoffice, not the firm. Only backoffice sees the
    // error + retry — for everyone else a failed request still reads as
    // "requested".
    if (failed && activeCapability === 'backoffice') {
      toast.update(toastId, {
        kind: 'err',
        message: 'No se pudo sincronizar la causa.',
        duration: 0,
        actionLabel: 'Reintentar',
        action: handleRetry,
      });
    } else {
      toast.update(toastId, {
        kind: 'success',
        message: 'Sincronización solicitada.',
        duration: 4000,
      });
    }
  }

  // The destructive API call + feedback, split out from the confirm step so
  // the error toast's "Reintentar" re-attempts the delete WITHOUT
  // re-prompting the type-to-confirm dialog (the user already confirmed once).
  async function runDelete() {
    setBusy(true);
    try {
      await deleteCase(caseId);
      queryClient.invalidateQueries({ queryKey: qk.cases.all() });
      toast({ kind: 'success', message: 'Dejaste de seguir la causa.' });
      navigate('/causas');
    } catch (err) {
      console.error('deleteCase failed', err);
      // 409 = la causa tiene un deal (cotización/pago) asociado y el backend
      // la protege para preservar el registro DTE/tributario. El resto:
      // genérico. Sin auto-cierre (duration 0) + reintento, porque es
      // accionable.
      const message =
        err?.status === 409
          ? 'No se puede dejar de seguir: la causa tiene una cotización o pago asociado.'
          : 'No se pudo dejar de seguir la causa.';
      toast({
        kind: 'err',
        message,
        duration: 0,
        actionLabel: 'Reintentar',
        action: runDelete,
      });
    } finally {
      setBusy(false);
    }
  }

  // Delete = unfollow (deleteCase). Type-to-confirm "ELIMINAR" first.
  async function handleDelete() {
    if (busy) return;
    const ok = await confirm({
      title: 'Dejar de seguir esta causa',
      body: 'Se quitará de tu lista. La causa se elimina y no podrás volver a seguirla durante 24 horas.',
      keyword: 'ELIMINAR',
      confirmLabel: 'Dejar de seguir',
    });
    if (!ok) return;
    runDelete();
  }

  return (
    <>
      <CausaDetalle
        causa={causa}
        actuaciones={actuaciones}
        actingsFailed={actingsFailed}
        diligencias={diligencias}
        activeDiligencia={activeDiligencia}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onViewActuacion={(row) =>
          row.fileUrl &&
          window.open(row.fileUrl, '_blank', 'noopener,noreferrer')
        }
        onViewDiligencia={(id) => navigate(`/diligencias/${id}`)}
        onNuevaDiligencia={() =>
          navigate('/diligencias/nueva', {
            state: {
              caseId,
              rol: causa.rol,
              caratula: causa.partes,
              courtName: rawCase?.courtName ?? null,
              tribunalName: rawCase?.tribunalName ?? null,
              // Keeps this causa detail mounted under the create modal (VD-8).
              background: location,
            },
          })
        }
        nuevaDisabledReason={nuevaDisabledReason}
        verificationStatus={rawCase?.verificationStatus}
        whatsappNumber={WHATSAPP_NUMBER}
        encargado={encargadoNombre}
        onAsignarEncargado={
          canAssign && admitsAssignee(verif)
            ? () => setShowAsignar(true)
            : undefined
        }
        canResync={canResync}
        syncing={syncing}
        onSync={handleRetry}
        onDelete={handleDelete}
      />
      {canAssign && admitsAssignee(verif) && (
        <AsignarEncargadoModal
          open={showAsignar}
          onClose={() => setShowAsignar(false)}
          members={members}
          currentEncargadoId={encargadoId}
          onSave={(assigneeId) => assignMutation.mutate({ assigneeId })}
          saving={assignMutation.isPending}
        />
      )}
    </>
  );
}
